import { useParams, Link } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { Lightbulb, Search, Filter, Sparkles, Dices, Package, Gift, Loader2, User, ChevronDown, ChevronUp, Target, Zap, MessageSquare, TrendingUp, Eye, ShieldCheck, Save, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/lib/picker-search";
import { useToast } from "@/hooks/use-toast";
import type { IdeationRun, Product, Offer } from "@shared/schema";
import { useState, useMemo } from "react";

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  draft: { label: "Draft", variant: "outline" },
  processing: { label: "Processing", variant: "secondary" },
  complete: { label: "Complete", variant: "default" },
  archived: { label: "Archived", variant: "secondary" },
  failed: { label: "Failed", variant: "destructive" },
};

export default function IdeatorListPage() {
  const { brandId } = useParams<{ brandId: string }>();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [ideas, setIdeas] = useState("");
  const [ideaItemType, setIdeaItemType] = useState<"product" | "offer">("product");
  const [luckyItemType, setLuckyItemType] = useState<"product" | "offer">("product");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedOfferId, setSelectedOfferId] = useState("");
  const [luckyProductId, setLuckyProductId] = useState("");
  const [luckyOfferId, setLuckyOfferId] = useState("");
  const [ideateResult, setIdeateResult] = useState<any>(null);
  const [savedPersonaIdx, setSavedPersonaIdx] = useState<Record<number, string>>({});

  const { data: savedResult } = useQuery({
    queryKey: ["/api/brands", brandId, "ideate-result"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId && !ideateResult,
  });

  const { data: runs, isLoading } = useQuery<IdeationRun[]>({
    queryKey: ["/api/brands", brandId, "ideation-runs"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  const { data: products } = useQuery<Product[]>({
    queryKey: ["/api/brands", brandId, "products"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  const { data: offers } = useQuery<Offer[]>({
    queryKey: ["/api/brands", brandId, "offers"],
    queryFn: getQueryFn({ on401: "returnNull" }),
    enabled: !!brandId,
  });

  // The persona-ideation Sim workflow runs for minutes, so the server kicks it
  // off as a background job and we poll a lightweight status endpoint instead of
  // holding one long request open (which the platform proxy would kill). The
  // promise stays unresolved until the job is ready/failed, so the calling
  // mutation's `isPending` keeps the existing spinner visible throughout.
  async function runIdeateJob(
    item: { productId?: string; offerId?: string },
    userInput: string,
  ): Promise<any> {
    const startRes = await apiRequest("POST", `/api/brands/${brandId}/ideate-personas`, {
      ...(item.offerId ? { offerId: item.offerId } : { productId: item.productId }),
      userInput,
    });
    const { jobId } = await startRes.json();
    if (!jobId) throw new Error("Could not start persona generation. Please try again.");

    const deadline = Date.now() + 16 * 60_000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 3000));
      const statusRes = await apiRequest("GET", `/api/brands/${brandId}/ideate-personas/${jobId}`);
      const status = await statusRes.json();
      if (status?.status === "completed") return status.result;
      if (status?.status === "failed") {
        throw new Error(status.message || "Persona generation failed. Please try again.");
      }
    }
    throw new Error("Persona generation timed out. Please try again.");
  }

  const ideatePersonasMutation = useMutation({
    mutationFn: async ({ productId, offerId, userInput }: { productId?: string; offerId?: string; userInput: string }) =>
      runIdeateJob({ productId, offerId }, userInput),
    onSuccess: (data) => {
      setIdeateResult(data);
      toast({ title: "Persona ideas generated" });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const savePersonaMutation = useMutation({
    mutationFn: async ({ persona, idx }: { persona: any; idx: number }) => {
      const slug = (persona.name || `persona-${idx + 1}`)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .slice(0, 80) + `-${Date.now().toString(36)}`;

      const noteParts: string[] = ["Saved from Persona Ideator."];
      if (persona.archetype) noteParts.push(`Archetype: ${persona.archetype}`);
      if (persona.funnel_role) noteParts.push(`Funnel role: ${persona.funnel_role}`);
      if (persona.closest_existing_persona?.reference) {
        noteParts.push(`vs. ${persona.closest_existing_persona.reference}: ${persona.closest_existing_persona.difference || ""}`.trim());
      }

      const body: any = {
        name: persona.name || `Persona ${idx + 1}`,
        slug,
        status: "draft",
        summary: persona.summary || "",
        worldview: persona.core_driver || "",
        purchaseDriversJson: persona.key_motivations || null,
        trustTriggersJson: persona.trust_triggers || null,
        objectionsJson: persona.key_objections || null,
        emotionalHooksJson: persona.angle_opportunities || null,
        notes: noteParts.join("\n"),
        // Kick off the full-profile Sim enrichment in the background so the
        // psychology fields (Primary Pain, Deep Fear, etc.) get filled in
        // instead of leaving the persona a slim sketch.
        enrichProfile: true,
      };
      const res = await apiRequest("POST", `/api/brands/${brandId}/personas`, body);
      const created = await res.json();
      return { created, idx };
    },
    onSuccess: ({ created, idx }) => {
      setSavedPersonaIdx((prev) => ({ ...prev, [idx]: created.id }));
      queryClient.invalidateQueries({ queryKey: ["/api/brands", brandId, "personas"] });
      toast({ title: "Saved to Personas", description: created.name });
    },
    onError: (e: Error) => {
      toast({ title: "Save failed", description: e.message, variant: "destructive" });
    },
  });

  const luckyMutation = useMutation({
    mutationFn: async (item: { productId?: string; offerId?: string }) => runIdeateJob(item, ""),
    onSuccess: (data) => {
      setIdeateResult(data);
      toast({ title: "Lucky persona hunt complete!" });
    },
    onError: (e: Error) => {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    },
  });

  const filteredRuns = useMemo(() => {
    if (!runs) return [];
    let result = runs.filter((r) => r.status !== "archived");
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) => r.name.toLowerCase().includes(q) || r.briefDescription?.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== "all") {
      result = result.filter((r) => r.status === statusFilter);
    }
    if (sortBy === "oldest") {
      result = [...result].sort((a, b) => new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime());
    }
    return result;
  }, [runs, search, statusFilter, sortBy]);

  const isAnyPending = ideatePersonasMutation.isPending || luckyMutation.isPending;
  const effectiveResult = ideateResult || savedResult;

  function tryParseResult(result: any): { personas: any[]; summary: any } | null {
    if (!result) return null;
    const root = result?.result || result;
    let personas: any[] = [];
    let summary: any = null;

    if (root?.personas && Array.isArray(root.personas)) {
      personas = root.personas;
      summary = root.workflow_summary || null;
    } else if (root?.output) {
      const output = typeof root.output === "string" ? JSON.parse(root.output) : root.output;
      if (Array.isArray(output)) personas = output;
      else if (output?.personas) {
        personas = output.personas;
        summary = output.workflow_summary || null;
      } else if (output?.result?.personas) {
        personas = output.result.personas;
        summary = output.result.workflow_summary || null;
      }
    } else if (Array.isArray(root)) {
      personas = root;
    }
    return personas.length > 0 ? { personas, summary } : null;
  }

  const parsed = tryParseResult(effectiveResult);
  const parsedPersonas = parsed?.personas || null;
  const workflowSummary = parsed?.summary || null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Lightbulb className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight" data-testid="text-page-title">Persona Ideator</h1>
            <p className="text-sm text-muted-foreground">Discover high-converting buyer personas for your brand</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-violet-500" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Describe Your Idea</h3>
            </div>
            <div
              className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60"
              data-testid="toggle-ideas-item"
            >
              <Button
                type="button"
                size="sm"
                variant={ideaItemType === "offer" ? "ghost" : "default"}
                className="h-6 px-2.5 text-[11px] gap-1 rounded-full"
                onClick={() => setIdeaItemType("product")}
                data-testid="toggle-ideas-product"
              >
                <Package className="h-3 w-3" /> Product
              </Button>
              <Button
                type="button"
                size="sm"
                variant={ideaItemType === "offer" ? "default" : "ghost"}
                className="h-6 px-2.5 text-[11px] gap-1 rounded-full"
                onClick={() => setIdeaItemType("offer")}
                data-testid="toggle-ideas-offer"
              >
                <Gift className="h-3 w-3" /> Offer
              </Button>
            </div>
          </div>
          <Textarea
            placeholder="What kind of buyer are you looking for? Describe your hypothesis, market signals, or the type of customer you want to reach..."
            className="min-h-[100px] resize-y"
            value={ideas}
            onChange={(e) => setIdeas(e.target.value)}
            data-testid="input-ideas"
          />
          {ideaItemType === "offer" ? (
            <SearchableSelect
              value={selectedOfferId}
              onValueChange={setSelectedOfferId}
              placeholder="Select an offer"
              testId="select-offer-ideas"
              triggerClassName="w-full"
              triggerIcon={<Gift className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />}
              options={(offers ?? []).map((o) => ({ value: o.id, label: o.name }))}
            />
          ) : (
            <SearchableSelect
              value={selectedProductId}
              onValueChange={setSelectedProductId}
              placeholder="Select a product"
              testId="select-product-ideas"
              triggerClassName="w-full"
              triggerIcon={<Package className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />}
              options={(products ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
          )}
          <Button
            className="rounded-full w-full"
            disabled={(ideaItemType === "offer" ? !selectedOfferId : !selectedProductId) || isAnyPending}
            onClick={() => {
              setIdeateResult(null);
              ideatePersonasMutation.mutate({
                ...(ideaItemType === "offer"
                  ? { offerId: selectedOfferId }
                  : { productId: selectedProductId }),
                userInput: ideas,
              });
            }}
            data-testid="button-generate-personas"
          >
            {ideatePersonasMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating...</>
            ) : (
              <><Sparkles className="h-4 w-4 mr-2" /> Generate Personas</>
            )}
          </Button>
        </div>

        <div className="rounded-2xl border bg-gradient-to-br from-violet-50/80 to-violet-100/40 dark:from-violet-950/30 dark:to-violet-900/20 border-violet-200/60 dark:border-violet-800/40 p-6 space-y-4 flex flex-col">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              <Dices className="h-4 w-4 text-violet-500" />
              <h3 className="text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400">I'm Feeling Lucky</h3>
            </div>
            <div
              className="flex items-center gap-1 p-0.5 rounded-full border border-border/70 bg-background/60"
              data-testid="toggle-lucky-item"
            >
              <Button
                type="button"
                size="sm"
                variant={luckyItemType === "offer" ? "ghost" : "default"}
                className="h-6 px-2.5 text-[11px] gap-1 rounded-full"
                onClick={() => setLuckyItemType("product")}
                data-testid="toggle-lucky-product"
              >
                <Package className="h-3 w-3" /> Product
              </Button>
              <Button
                type="button"
                size="sm"
                variant={luckyItemType === "offer" ? "default" : "ghost"}
                className="h-6 px-2.5 text-[11px] gap-1 rounded-full"
                onClick={() => setLuckyItemType("offer")}
                data-testid="toggle-lucky-offer"
              >
                <Gift className="h-3 w-3" /> Offer
              </Button>
            </div>
          </div>
          <p className="text-sm text-muted-foreground flex-1">
            Select {luckyItemType === "offer" ? "an offer" : "a product"} and let the AI autonomously hunt for high-converting persona opportunities based on your brand and {luckyItemType === "offer" ? "offer" : "product"} data.
          </p>
          {luckyItemType === "offer" ? (
            <SearchableSelect
              value={luckyOfferId}
              onValueChange={setLuckyOfferId}
              placeholder="Select an offer"
              testId="select-offer-lucky"
              triggerClassName="w-full"
              triggerIcon={<Gift className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />}
              options={(offers ?? []).map((o) => ({ value: o.id, label: o.name }))}
            />
          ) : (
            <SearchableSelect
              value={luckyProductId}
              onValueChange={setLuckyProductId}
              placeholder="Select a product"
              testId="select-product-lucky"
              triggerClassName="w-full"
              triggerIcon={<Package className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />}
              options={(products ?? []).map((p) => ({ value: p.id, label: p.name }))}
            />
          )}
          <Button
            variant="outline"
            className="rounded-full w-full border-violet-300 dark:border-violet-700 hover:bg-violet-100 dark:hover:bg-violet-900/40"
            disabled={(luckyItemType === "offer" ? !luckyOfferId : !luckyProductId) || isAnyPending}
            onClick={() =>
              luckyMutation.mutate(
                luckyItemType === "offer" ? { offerId: luckyOfferId } : { productId: luckyProductId },
              )
            }
            data-testid="button-feeling-lucky"
          >
            {luckyMutation.isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Hunting...</>
            ) : (
              <><Dices className="h-4 w-4 mr-2" /> I'm Feeling Lucky</>
            )}
          </Button>
        </div>
      </div>

      {effectiveResult && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5 text-violet-500" />
              <h2 className="text-lg font-bold">Generated Persona Ideas</h2>
              {parsedPersonas && (
                <Badge variant="secondary" className="text-xs">{parsedPersonas.length} personas</Badge>
              )}
            </div>
          </div>

          {workflowSummary?.top_whitespace_opportunities && (
            <div className="rounded-xl border border-violet-200/60 dark:border-violet-800/40 bg-violet-50/50 dark:bg-violet-950/20 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-violet-600 dark:text-violet-400 mb-2">Top Whitespace Opportunities</p>
              <ul className="space-y-1">
                {workflowSummary.top_whitespace_opportunities.map((opp: string, i: number) => (
                  <li key={i} className="text-xs text-muted-foreground flex gap-2">
                    <TrendingUp className="h-3 w-3 mt-0.5 shrink-0 text-violet-500" />
                    <span>{opp}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {parsedPersonas && parsedPersonas.length > 0 ? (
            <div className="space-y-4">
              {parsedPersonas.map((persona: any, i: number) => {
                const scores = persona.scores || {};
                const avgScore = scores.novelty && scores.commercial_value && scores.creative_potential
                  ? ((scores.novelty + scores.commercial_value + scores.creative_potential) / 3).toFixed(1)
                  : null;
                return (
                  <div key={i} className="rounded-2xl border bg-card overflow-hidden" data-testid={`card-persona-idea-${i}`}>
                    <div className="p-5 space-y-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="space-y-1 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-bold text-base">{persona.name || `Persona ${i + 1}`}</h3>
                            {persona.archetype && (
                              <Badge variant="outline" className="text-[10px] font-medium">{persona.archetype}</Badge>
                            )}
                          </div>
                          {persona.summary && (
                            <p className="text-sm text-muted-foreground leading-relaxed">{persona.summary}</p>
                          )}
                        </div>
                        {avgScore && (
                          <div className="shrink-0 flex flex-col items-center gap-0.5">
                            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 font-bold text-lg">
                              {avgScore}
                            </div>
                            <span className="text-[9px] text-muted-foreground">avg score</span>
                          </div>
                        )}
                      </div>

                      {(scores.novelty || scores.commercial_value || scores.creative_potential) && (
                        <div className="flex gap-3">
                          {scores.novelty && (
                            <div className="flex items-center gap-1.5 text-xs">
                              <Sparkles className="h-3 w-3 text-amber-500" />
                              <span className="text-muted-foreground">Novelty</span>
                              <span className="font-semibold">{scores.novelty}</span>
                            </div>
                          )}
                          {scores.commercial_value && (
                            <div className="flex items-center gap-1.5 text-xs">
                              <TrendingUp className="h-3 w-3 text-green-500" />
                              <span className="text-muted-foreground">Commercial</span>
                              <span className="font-semibold">{scores.commercial_value}</span>
                            </div>
                          )}
                          {scores.creative_potential && (
                            <div className="flex items-center gap-1.5 text-xs">
                              <Zap className="h-3 w-3 text-violet-500" />
                              <span className="text-muted-foreground">Creative</span>
                              <span className="font-semibold">{scores.creative_potential}</span>
                            </div>
                          )}
                        </div>
                      )}

                      {persona.core_driver && (
                        <div className="rounded-lg bg-muted/50 p-3">
                          <div className="flex items-center gap-1.5 mb-1">
                            <Target className="h-3 w-3 text-violet-500" />
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Core Driver</span>
                          </div>
                          <p className="text-sm">{persona.core_driver}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {persona.key_motivations && persona.key_motivations.length > 0 && (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Eye className="h-3 w-3 text-blue-500" />
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Motivations</span>
                            </div>
                            <ul className="space-y-1">
                              {persona.key_motivations.map((m: string, j: number) => (
                                <li key={j} className="text-xs text-muted-foreground pl-4 relative before:content-[''] before:absolute before:left-1 before:top-[7px] before:h-1 before:w-1 before:rounded-full before:bg-blue-400">{m}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {persona.trust_triggers && persona.trust_triggers.length > 0 && (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <ShieldCheck className="h-3 w-3 text-green-500" />
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Trust Triggers</span>
                            </div>
                            <ul className="space-y-1">
                              {persona.trust_triggers.map((t: string, j: number) => (
                                <li key={j} className="text-xs text-muted-foreground pl-4 relative before:content-[''] before:absolute before:left-1 before:top-[7px] before:h-1 before:w-1 before:rounded-full before:bg-green-400">{t}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {persona.key_objections && persona.key_objections.length > 0 && (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <MessageSquare className="h-3 w-3 text-orange-500" />
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Key Objections</span>
                            </div>
                            <ul className="space-y-1">
                              {persona.key_objections.map((o: string, j: number) => (
                                <li key={j} className="text-xs text-muted-foreground italic pl-4 relative before:content-[''] before:absolute before:left-1 before:top-[7px] before:h-1 before:w-1 before:rounded-full before:bg-orange-400">{o}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {persona.angle_opportunities && persona.angle_opportunities.length > 0 && (
                          <div className="space-y-1.5">
                            <div className="flex items-center gap-1.5">
                              <Zap className="h-3 w-3 text-violet-500" />
                              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Angle Opportunities</span>
                            </div>
                            <ul className="space-y-1">
                              {persona.angle_opportunities.map((a: string, j: number) => (
                                <li key={j} className="text-xs text-muted-foreground pl-4 relative before:content-[''] before:absolute before:left-1 before:top-[7px] before:h-1 before:w-1 before:rounded-full before:bg-violet-400">{a}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      {persona.closest_existing_persona && (
                        <div className="rounded-lg border border-dashed p-3 space-y-1">
                          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">vs. {persona.closest_existing_persona.reference || "Existing Library"}</p>
                          <p className="text-xs text-muted-foreground">{persona.closest_existing_persona.difference}</p>
                        </div>
                      )}

                      <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
                        <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                          {persona.funnel_role && (
                            <span className="flex items-center gap-1"><TrendingUp className="h-3 w-3" /> {persona.funnel_role}</span>
                          )}
                        </div>
                        {savedPersonaIdx[i] ? (
                          <Link href={`/brands/${brandId}/personas/${savedPersonaIdx[i]}`}>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs gap-1.5"
                              data-testid={`button-view-saved-persona-${i}`}
                            >
                              <Check className="h-3 w-3 text-green-600" />
                              Saved — View
                            </Button>
                          </Link>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-xs gap-1.5"
                            disabled={savePersonaMutation.isPending}
                            onClick={() => savePersonaMutation.mutate({ persona, idx: i })}
                            data-testid={`button-save-persona-${i}`}
                          >
                            {savePersonaMutation.isPending && savePersonaMutation.variables?.idx === i ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <Save className="h-3 w-3" />
                            )}
                            Save to Personas
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-xl border p-4">
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                {typeof effectiveResult === "string" ? effectiveResult : JSON.stringify(effectiveResult?.result?.output || effectiveResult?.result || effectiveResult, null, 2)}
              </p>
            </div>
          )}

        </div>
      )}

      {(runs && runs.length > 0) && (
        <>
          <div className="flex items-center gap-3 pt-2">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search runs..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="input-search-runs"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[140px]" data-testid="select-status-filter">
                <Filter className="h-3.5 w-3.5 mr-1.5 text-muted-foreground" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="draft">Draft</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[130px]" data-testid="select-sort">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">Newest</SelectItem>
                <SelectItem value="oldest">Oldest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-48 rounded-xl" />
              ))}
            </div>
          ) : filteredRuns.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No matching runs found.</p>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {filteredRuns.map((run) => {
                const cfg = STATUS_CONFIG[run.status] || STATUS_CONFIG.draft;
                return (
                  <Link key={run.id} href={`/brands/${brandId}/ideator/${run.id}`}>
                    <div
                      className="group relative rounded-xl border bg-card p-5 hover:shadow-md transition-all cursor-pointer"
                      data-testid={`card-ideation-run-${run.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <h3 className="font-semibold text-sm line-clamp-1 group-hover:text-primary transition-colors" data-testid={`text-run-name-${run.id}`}>
                          {run.name}
                        </h3>
                        <Badge variant={cfg.variant} className="text-[10px] shrink-0 ml-2" data-testid={`badge-status-${run.id}`}>
                          {cfg.label}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground line-clamp-2 mb-4" data-testid={`text-run-brief-${run.id}`}>
                        {run.briefDescription}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span data-testid={`text-total-${run.id}`}>{run.totalCandidates || 0} candidates</span>
                        {(run.keptCount ?? 0) > 0 && <span className="text-green-600">{run.keptCount} kept</span>}
                        {(run.rejectedCount ?? 0) > 0 && <span className="text-red-500">{run.rejectedCount} rejected</span>}
                        {(run.mergedCount ?? 0) > 0 && <span className="text-blue-500">{run.mergedCount} merged</span>}
                      </div>
                      {run.category && (
                        <div className="mt-3">
                          <Badge variant="outline" className="text-[10px]">{run.category}</Badge>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
